import React, { FC, useEffect, useState } from 'react';
import { Dimensions, ScrollView, Text, View } from 'react-native';
import i18n from 'i18n-js';
import { connect } from 'react-redux';

import { scale as initialScale } from '@components/frame';
import ColorWrapper from '@styles/colors';
import { font as initialFont } from '@styles/fonts';
import Svg from '@styles/icons/svg';
import ProductionInformation from '@components/productionInformation';
import { PanelProps } from '@components/operatorPanel/components/panel';
import { isPhone } from '@helpers';
import TouchableElement from '@components/common/touchableElement';
import { SmallLoading } from '@components/loading';
import { SCREEN_BORDER_RADIUS, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { MoreInfo } from '@components/common/NCFileType';
import { ignoreProductionInformation, setChangeProductionInformation, setSetupProductionInformation } from '@reducers/manufacture';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { setSelectedNCFile } from '@reducers/machines';
import { setShouldShowGlobalFullNCFileButton } from '@reducers/machinesV2';
import NcFileContent from '@components/common/NCFile';
import { ColorFunction } from '@styles/colors/types';
import { getBoxShadowStyle } from '@styles/shadow';
import { NC_FILE_TYPE_OPTIONS } from '@components/common/NCFileType/constants';
import { isSimpleType, TYPE_DESCRIPTIONS } from '@components/common/NCFileType/NCFileWizardNoERP/constants';
import type { NCFileTypeOption } from '@components/common/NCFileType/NCFileWizardNoERP/types';

interface OperatorPanelNCFileType extends Pick<PanelProps, 'CC' | 'selectedItem' | 'scale' | 'font'> {
  C: ColorFunction;
  hide: () => void;
  productionBatchId: number;
  ncFileId: number;
  ncFileName: string;
  setShouldShowGlobalFullNCFileButton: (state: boolean) => void;
  onActionStart?: () => void;
  onActionDone?: () => void;
  isNcFileContentAvailable?: boolean;
}

const OperatorPanelNCFileType: FC<OperatorPanelNCFileType> = ({
  CC,
  C,
  selectedItem,
  hide,
  productionBatchId,
  ignoreProductionInformation,
  setChangeProductionInformation,
  ncFileId: _ncFileId,
  ncFileName: _ncFileName,
  setShouldShowGlobalFullNCFileButton,
  isNcFileContentAvailable: isNcFileContentAvailableInitial,
  scale = initialScale,
  font = initialFont,
  onActionStart,
  onActionDone,
}) => {
  const dispatch = useAppDispatch();
  const [selectedType, setSelectedType] = useState<NCFileTypeOption>('linked');
  const [loading, setLoading] = useState(false);
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [showProductionInfo, setShowProductionInfo] = useState(false);
  const [isNcFileContentAvailable, setIsNcFileContentAvailable] = useState<boolean>(!!isNcFileContentAvailableInitial);

  const ncFileId = _ncFileId ?? selectedItem?.machineStatus?.ncFileId;
  const ncFileName = _ncFileName ?? selectedItem?.machineStatus?.ncFile ?? selectedItem?.machineStatus?.ncFileName;

  useEffect(() => {
    setShouldShowGlobalFullNCFileButton(false);
    return () => setShouldShowGlobalFullNCFileButton(true);
  }, []);

  const nextName = selectedType === 'linked' ? 'Next' : isSimpleType(selectedType) ? 'Confirm' : '';

  const done = () => {
    setLoading(false);
    hide();
    onActionDone?.();
  };

  const submitAction = async () => {
    const data = {
      ncFileId,
    };

    switch (selectedType) {
      case 'linked': {
        setShowProductionInfo(true);
        return;
      }
      case 'change': {
        setLoading(true);
        if (onActionStart) {
          onActionStart();
        }
        await setChangeProductionInformation(data);
        done();
        return;
      }
      case 'ignore': {
        setLoading(true);
        if (onActionStart) {
          onActionStart();
        }
        await ignoreProductionInformation(data);
        done();
        return;
      }
      case 'setup': {
        setLoading(true);
        if (onActionStart) {
          onActionStart();
        }
        await dispatch(setSetupProductionInformation(data));
        done();
        return;
      }
      default:
        return '';
    }
  };

  if (showProductionInfo) {
    return (
      <View style={isPhone ? { flex: 1 } : { height: '100%' }}>
        <ProductionInformation
          {...{
            selectedNCFileId: ncFileId,
            onBackPress: () => setShowProductionInfo(false),
            hide,
            productionBatchId,
            mainBg: 'Background/Light/System',
            isOperatorPanel: true,
            title: 'Link Batch to Job',
            isNcFileContentAvailable,
            onActionStart,
            scale,
            font,
          }}
        />
      </View>
    );
  }

  const fullWidth = Dimensions.get('window')?.width;
  const width = fullWidth > scale(970) ? scale(686) : fullWidth > scale(850) ? scale(600) : fullWidth > 450 ? fullWidth / 1.5 : fullWidth / 2;

  return (
    <View testID={TEST_IDS.operatorPanel.ncFileTypeModal.root} style={{ flex: 1 }}>
      <ScrollView contentContainerStyle={{ minHeight: '100%' }}>
        <View style={{ height: '100%', backgroundColor: C('Background/Dark/Modal'), paddingTop: scale(18), flex: 1 }}>
          <View
            style={{
              width: '100%',
              zIndex: 1000,
              flexDirection: 'row',
              justifyContent: 'center',
            }}
          >
            <TouchableElement onPress={hide}>
              {({ boxStyle, pressHoverColor }) => (
                <View
                  style={[
                    boxStyle,
                    {
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      paddingHorizontal: scale(18),
                      paddingVertical: scale(10),
                      flexDirection: 'row',
                      backgroundColor: C(pressHoverColor('Button/Dark/Tertiary')),
                      marginLeft: scale(24),
                      borderRadius: scale(20),
                    },
                  ]}
                >
                  <Text style={[{ color: C('Button/Dark/Tertiary Text') }, font['Headline Large']]}>{i18n.t('Cancel')}</Text>
                </View>
              )}
            </TouchableElement>

            <View
              style={[
                {
                  backgroundColor: '#D6B02B',
                  borderRadius: scale(45),
                  alignItems: 'center',
                  paddingTop: scale(30),
                  paddingBottom: scale(18),
                  width,
                },
              ]}
            >
              <Text style={[font['Large Title'], { color: CC('Label/Light/AlwaysDark Primary'), marginBottom: scale(9) }]}>
                {i18n.t('Select NC File Type')}
              </Text>
              <Text style={[font.Headline, { color: CC('Label/Light/AlwaysDark Primary'), marginBottom: scale(34) }]}>{i18n.t(ncFileName)}</Text>

              <View style={{ paddingHorizontal: scale(93), width: '100%' }}>
                <View
                  style={[
                    getBoxShadowStyle({ type: 'Very Wide', C }),
                    {
                      backgroundColor: C('Background/Dark/Field lighter'),
                      borderRadius: scale(27),
                      width: '100%',
                      flexDirection: 'row',
                      padding: scale(18),
                    },
                  ]}
                >
                  {NC_FILE_TYPE_OPTIONS.map((t) => {
                    const selected = selectedType === t.icon;

                    return (
                      <View key={t.name} style={{ flex: 1, justifyContent: 'center' }}>
                        <TouchableElement
                          onPress={() => {
                            if (loading) {
                              return;
                            }
                            setSelectedType(t.icon);
                          }}
                          hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}
                        >
                          {({ boxStyle, pressHoverColor }) => (
                            <View
                              style={[
                                boxStyle,
                                {
                                  width: '100%',
                                  alignItems: 'center',
                                  paddingVertical: scale(18),
                                  borderWidth: scale(2),
                                  borderRadius: scale(SCREEN_BORDER_RADIUS),
                                  borderColor: selected ? CC('Selected/Light/Primary') : 'transparent',
                                  backgroundColor: selected ? CC('Fill/Dark/Secondary') : 'transparent',
                                },
                              ]}
                            >
                              <Svg icon={`${t.icon}${selected ? 'Active' : 'Inactive'}`} width={scale(48)} height={scale(48)} />
                              <Text style={[{ color: C('Label/Light/Primary'), marginVertical: scale(7) }, font['Subhead 1']]}>{i18n.t(t.name)}</Text>
                              <Svg
                                icon={selected ? 'selected' : 'unselected'}
                                width={scale(22)}
                                height={scale(22)}
                                color={selected ? 'Selected / Light / Primary' : 'Label/Light/Quinary'}
                              />
                            </View>
                          )}
                        </TouchableElement>
                      </View>
                    );
                  })}
                </View>
              </View>
            </View>

            <TouchableElement
              onPress={() => {
                if (loading) {
                  return;
                }
                submitAction();
              }}
            >
              {({ boxStyle, pressHoverColor }) => (
                <View
                  style={[
                    boxStyle,
                    {
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      paddingHorizontal: scale(18),
                      paddingVertical: scale(10),
                      backgroundColor: C(pressHoverColor('Button/Dark/PrimaryMono')),
                      borderRadius: scale(20),
                      alignItems: 'center',
                      marginRight: scale(24),
                    },
                  ]}
                >
                  {loading ? (
                    <SmallLoading />
                  ) : (
                    <Text style={[{ color: C('Button/Dark/Mono Icon Text'), textAlign: 'center' }, font['Headline Large']]}>{i18n.t(nextName)}</Text>
                  )}
                </View>
              )}
            </TouchableElement>
          </View>

          <View style={{ marginTop: scale(8), marginHorizontal: 'auto', flex: 1, width }}>
            <View style={{ paddingHorizontal: scale(15), marginTop: scale(6) }}>
              <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>{i18n.t(TYPE_DESCRIPTIONS[selectedType])}</Text>
              {!showMoreInfo && (
                <TouchableElement
                  onPress={() => {
                    setShowMoreInfo(true);
                  }}
                  hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}
                >
                  {({ boxStyle }) => (
                    <View style={[boxStyle, {}]}>
                      <Text style={[{ color: CC('Link/Light/Primary') }, font.Footnote]}>{i18n.t('More info')}</Text>
                    </View>
                  )}
                </TouchableElement>
              )}
            </View>
            <View
              style={[
                {
                  minWidth: '100%',
                  width: '100%',
                  paddingTop: scale(15),
                  justifyContent: 'center',
                  alignItems: 'center',
                  flex: 1,
                },
              ]}
            >
              {showMoreInfo ? (
                <View style={{ flex: 1, width: '100%' }}>
                  <View style={{ backgroundColor: C('Background/Dark/Field lighter'), borderRadius: scale(SCREEN_BORDER_RADIUS) }}>
                    <View
                      style={{
                        zIndex: 100,
                        position: 'absolute',
                        right: scale(9),
                        top: scale(9),
                      }}
                    >
                      <TouchableElement
                        onPress={() => {
                          setShowMoreInfo(false);
                        }}
                        hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}
                      >
                        {({ boxStyle }) => (
                          <View
                            style={[
                              boxStyle,
                              {
                                backgroundColor: CC('Button/Light/Secondary'),
                                width: scale(27),
                                height: scale(27),
                                borderRadius: scale(27) / 2,
                                justifyContent: 'center',
                                alignItems: 'center',
                              },
                            ]}
                          >
                            <Svg icon="close" width={scale(11)} height={scale(11)} color={'Button/Light/Secondary Text'} />
                          </View>
                        )}
                      </TouchableElement>
                    </View>
                    <MoreInfo {...{ selectedType, C }} />
                  </View>
                </View>
              ) : (
                <View style={{ flex: 1, width: '100%', height: '100%' }}>
                  <NcFileContent
                    {...{
                      selectedItem,
                      C,
                      font,
                      scale,
                      backgroundColor: C('Background/Dark/Modal'),
                      ncFileId,
                      ncFileName,
                      showNcFileButtonCallBackOnPress: hide,
                      isOperatorPanel: true,
                      singleColor: true,
                      ncFileTextColor: C('Label/Light/Secondary'),
                      isPreview: false,
                      setIsNcFileContentAvailable,
                    }}
                  />
                </View>
              )}
            </View>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

export default connect(({ membership }) => ({ membership }), {
  ignoreProductionInformation,
  setChangeProductionInformation,
  setSelectedNCFile,
  setShouldShowGlobalFullNCFileButton,
})(ColorWrapper(OperatorPanelNCFileType));
